import React, { useState } from 'react';
import {
  Box,
  Collapse,
  Grid,
  Skeleton,
  Typography,
  useMediaQuery,
} from '@mui/material';
import ArrowDownIcon from '../../../../components/Icons/ArrowDownIcon';
import { useThemeContext } from '../../../../theme/ThemeContextProvider';
import { useGetBadgesByUserIdQuery } from '../../../../store/apiSlice/badgeApi';
import { useSelector } from 'react-redux';
import { selectAuthData } from '../../../../store/selectors/authSelector';
import { useCurrentPageUserId } from '../../../../hooks/useCurrentPageUserId';
import { UserRoleEnum } from '../../../../enums/user-data-field-enum';
import { IBadgeResponse } from '../../../../interfaces/badge.interface';
import UserBadge from './UserBadge/UserBadge';

const UserBadges = () => {
  const BADGES = 'Badges';
  const [isScoreExpanded, setIsScoreExpanded] = useState(false);
  const authData = useSelector(selectAuthData);
  const { userId } = useCurrentPageUserId();
  const role = authData?.user?.role;

  const { data: badges, isLoading } = useGetBadgesByUserIdQuery(userId || '', {
    skip: !userId || role !== UserRoleEnum.USER,
  });

  const { theme } = useThemeContext();

  const mobileView = useMediaQuery(theme.breakpoints.down('lg'));
  const toggleExpand = () => {
    setIsScoreExpanded((prevState) => !prevState);
  };

  const Badges = (
    <Box
      display="flex"
      flexDirection="column"
      gap="36px"
      sx={{
        padding: '22px 16px',
      }}
    >
      <Typography component="h3" variant="h3">
        {BADGES}
      </Typography>

      <Grid container spacing={2} justifyContent="center">
        {badges &&
          badges.map((badge: IBadgeResponse, index: number) => (
            <Grid
              item
              key={badge.id}
              xs={12}
              sm={
                badges.length % 2 !== 0 && index === badges.length - 1 ? 6 : 6
              }
              display="flex"
              justifyContent={
                badges.length % 2 !== 0 && index === badges.length - 1
                  ? 'center'
                  : 'flex-start'
              }
            >
              <UserBadge badge={badge} />
            </Grid>
          ))}
      </Grid>
    </Box>
  );

  return (
    <>
      {isLoading ? (
        <Box
          display="flex"
          flexDirection="column"
          bgcolor="background.white"
          sx={{ borderRadius: '1px', p: { xs: '0', lg: '36px 22px' } }}
        >
          {skeletonBadges}
        </Box>
      ) : badges?.length ? (
        <Box
          display="flex"
          flexDirection="column"
          bgcolor="background.white"
          sx={{ borderRadius: '1px', p: '0' }}
          onClick={toggleExpand}
        >
          {mobileView && (
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              sx={{
                borderBottom: '1px solid',
                borderColor: 'background.light',
                p: '16px',
              }}
            >
              <Typography variant="badge2" textTransform="uppercase">
                {BADGES}
              </Typography>
              <ArrowDownIcon
                sx={{
                  width: '28px',
                  height: '28px',
                  transform: isScoreExpanded ? 'rotate(180deg)' : 'none',
                }}
              />
            </Box>
          )}

          {mobileView ? (
            <Collapse in={isScoreExpanded}>{Badges}</Collapse>
          ) : (
            Badges
          )}

          {mobileView && (
            <Box
              sx={{ backgroundColor: 'background.default', height: '16px' }}
            />
          )}
        </Box>
      ) : null}
    </>
  );
};

export default UserBadges;

const skeletonBadges = (
  <Box
    sx={{
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      gap: '36px',
      p: { xs: '22px 16px', lg: '0' },
    }}
  >
    <Skeleton
      variant="rectangular"
      sx={{
        width: '100%',
        height: '2rem',
      }}
    />

    <Skeleton
      variant="rectangular"
      sx={{
        width: '100%',
        height: '25rem',
      }}
    />
  </Box>
);
